import type { ReactNode } from "react";
import { Info } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

interface InfoPopoverProps {
  /** Section or field name; the button is labelled "About {title}". */
  title: string;
  /** Short helper lines, one paragraph each. Falsy entries are skipped. */
  lines: Array<ReactNode | false | null | undefined>;
}

/**
 * Click-to-open "i" icon that holds a section's helper text, so the page stays
 * quiet until someone wants the explanation. Closes on outside click or Esc.
 */
export const InfoPopover = ({ title, lines }: InfoPopoverProps) => {
  const shown = lines.filter(Boolean);
  if (shown.length === 0) return null;
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-5 text-muted-foreground"
          aria-label={`About ${title}`}
        >
          <Info className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="max-w-xs space-y-2 p-3">
        {shown.map((line, i) => (
          <p key={i} className="z-caption text-muted-foreground">
            {line}
          </p>
        ))}
      </PopoverContent>
    </Popover>
  );
};

export default InfoPopover;
